import { useEffect, useState } from 'react';
import { Shield, AlertCircle, Plus } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { complianceApi, ApiError } from '@solara/api-client';
import type { RegulatoryInsuranceDto } from '@solara/api-client';

const INSURANCE_TYPES = ['PROFESSIONAL_INDEMNITY', 'CRIME_INSURANCE', 'CYBER_FRAUD_INSURANCE'] as const;

export default function InsurancesPage() {
  const { t } = useTranslation('p1');
  const [items, setItems] = useState<RegulatoryInsuranceDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<RegulatoryInsuranceDto & { coverageAed: number }>>({
    insuranceType: 'PROFESSIONAL_INDEMNITY',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setItems(await complianceApi.insurances());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function add() {
    if (!draft.policyNumber || !draft.insurer || !draft.effectiveFrom || !draft.effectiveTo || !draft.coverageAed) {
      setError(t('common.fieldRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await complianceApi.createInsurance({
        insuranceType: draft.insuranceType ?? 'PROFESSIONAL_INDEMNITY',
        policyNumber: draft.policyNumber,
        insurer: draft.insurer,
        effectiveFrom: draft.effectiveFrom,
        effectiveTo: draft.effectiveTo,
        coverageAedMinor: Math.round(draft.coverageAed * 100),
      });
      setDraft({ insuranceType: 'PROFESSIONAL_INDEMNITY' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('compliance.insurancesTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('compliance.insurancesSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t('compliance.insurances.policiesCount', { count: items.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('compliance.insurances.noPolicies')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((i) => (
                <li key={i.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium">
                      {t(`compliance.insurances.types.${i.insuranceType}`, {
                        defaultValue: i.insuranceType.replace(/_/g, ' '),
                      })}
                    </p>
                    <p className="text-xs text-fg-muted">
                      {i.insurer} · #{i.policyNumber} ·{' '}
                      {t('compliance.insurances.coverageAmount')}{' '}
                      {(i.coverageAedMinor / 100).toLocaleString()} AED
                    </p>
                  </div>
                  <Badge
                    tone={new Date(i.effectiveTo) < new Date() ? 'danger' : 'neutral'}
                    className="text-[10px]"
                  >
                    {t('compliance.insurances.validUntil')}{' '}
                    {new Date(i.effectiveTo).toLocaleDateString()}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t('compliance.insurances.addPolicy')}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={draft.insuranceType ?? 'PROFESSIONAL_INDEMNITY'}
            onChange={(e) => setDraft({ ...draft, insuranceType: e.target.value })}
          >
            {INSURANCE_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`compliance.insurances.types.${ty}`, { defaultValue: ty.replace(/_/g, ' ') })}
              </option>
            ))}
          </select>
          <Input
            placeholder={t('compliance.insurances.policyNumber')}
            value={draft.policyNumber ?? ''}
            onChange={(e) => setDraft({ ...draft, policyNumber: e.target.value })}
          />
          <Input
            placeholder={t('compliance.insurances.insurer')}
            value={draft.insurer ?? ''}
            onChange={(e) => setDraft({ ...draft, insurer: e.target.value })}
          />
          <Input
            type="number"
            placeholder={t('compliance.insurances.coverageAmount')}
            value={draft.coverageAed ?? ''}
            onChange={(e) => setDraft({ ...draft, coverageAed: Number(e.target.value) })}
          />
          <Input
            type="date"
            placeholder={t('compliance.insurances.validFrom')}
            value={draft.effectiveFrom ?? ''}
            onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
          />
          <Input
            type="date"
            placeholder={t('compliance.insurances.validUntil')}
            value={draft.effectiveTo ?? ''}
            onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })}
          />
          <div className="sm:col-span-2">
            <Button onClick={add} disabled={busy}>
              {busy ? t('actions.saving') : t('compliance.insurances.addPolicy')}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
